import React, { Dispatch, SetStateAction } from "react"

import { Button, Chip, Grid, IconButton, InputAdornment, TextField } from "@material-ui/core"
import { ClientCatalogWhereData } from "@/pages/Admin/ClientCatalog"
import ContactFilters from "@/pages/Admin/ClientCatalog/ClientManageFilters/ContactsFilters"
import ContactAction from "@/components/ContactAction"
import useValidation from "@/hooks/useValidation"
import {
	Search as SearchIcon,
	Close as CloseIcon,
	Delete as TrashIcon
} from "@material-ui/icons"
import { colors } from "@/styles/colors"
import { BaseContactsFiltersType } from "@/protocols/ClientCatalogFilterProtocol"
import { useGlobalStateStore } from "@/store/GlobalState"
import DisableContactDialog from "@/pages/Admin/ClientCatalog/DisableContactDialog"
import ContactGroupPermissionGate from "@/components/PermissionGate/ContactGroupPermissionGate"
import useCustomStyles from "@/styles/custom"
import ColorUtil from "@/utils/ColorUtil"

type ClientManageContactsProps = {
	getAndUpdateClientCatalogData: (newWhereData?: Partial<ClientCatalogWhereData>) => Promise<void>
	setWhereData: Dispatch<SetStateAction<ClientCatalogWhereData>>
	whereData: ClientCatalogWhereData
	hasContactsOnCurrentFilter: boolean
}

const ClientManageContacts: React.FC<ClientManageContactsProps> = (props) => {
	const {
		getAndUpdateClientCatalogData,
		setWhereData,
		hasContactsOnCurrentFilter,
		whereData
	} = props

	const { validation } = useValidation()
	const isSelectedContactsFilterEmpty = Boolean(whereData?.contactsFilters)
	const handleSearchChange = async (searchData: string) => {
		setWhereData({
			search: searchData,
			page: 0,
			rowsPerPage: 20
		})
	}
	const globalStateStore = useGlobalStateStore()
	const customClasses = useCustomStyles()
	const inboxChannelId = globalStateStore.currentChannel?.id

	const handleRemoveSelectedContactFilters = async () => {
		setWhereData({ ...whereData, contactsFilters: {} as BaseContactsFiltersType })

		await getAndUpdateClientCatalogData({ contactsFilters: undefined })
	}

	const handleUpdateFilterData = (updatedFilter: BaseContactsFiltersType) => {
		setWhereData({ ...whereData, contactsFilters: updatedFilter })
	}

	return (
		<>
			<Grid container spacing={1}>
				<Grid item xs={12}>
					<Grid container spacing={2} alignItems="center" justifyContent="space-between">
						<Grid item>
							<Grid container alignItems="center" spacing={2}>
								<Grid item>
									<ContactFilters
										filter={isSelectedContactsFilterEmpty ? { ...whereData?.contactsFilters, inboxChannelId } : undefined}
										handleUpdateFilterData={handleUpdateFilterData}
										getDataWithFilters={getAndUpdateClientCatalogData} />
								</Grid>
								<Grid item>
									<ContactAction
										contactAction={"filter"}
										contactFilters={whereData?.contactsFilters}
										hasContactsOnCurrentFilter={hasContactsOnCurrentFilter}
									/>
								</Grid>
								<Grid item>
									<ContactGroupPermissionGate action="disable">
										<DisableContactDialog
											type="many-contacts"
											disabled={Boolean(!hasContactsOnCurrentFilter || !isSelectedContactsFilterEmpty)}
											filterConditions={whereData?.contactsFilters}
											onUpdateContactsList={handleRemoveSelectedContactFilters}
										>
											<Button
												variant="contained"
												className={customClasses.secondaryActionButton}
												startIcon={<TrashIcon />}
												disabled={Boolean(!hasContactsOnCurrentFilter || !isSelectedContactsFilterEmpty)}
											>
												Excluir Contatos
											</Button>
										</DisableContactDialog>
									</ContactGroupPermissionGate>
								</Grid>
							</Grid>
						</Grid>
						<Grid item xs={4}>
							<TextField
								value={whereData.search}
								onChange={({ target }) =>
									handleSearchChange(target.value)
								}
								placeholder="Pesquisar"
								variant="outlined"
								InputProps={{
									startAdornment: (
										<InputAdornment position="start">
											<SearchIcon />
										</InputAdornment>
									),
									endAdornment: whereData.search && (
										<IconButton
											size="small"
											onClick={() => handleSearchChange("")}
										>
											<CloseIcon
												fontSize="small"
											/>
										</IconButton>
									)
								}}
								fullWidth
								size="small"
								helperText={validation.search}
								error={!!validation.search}
							/>
						</Grid>
					</Grid>
				</Grid>

				<Grid item>
					{isSelectedContactsFilterEmpty && <Grid container alignItems="center" spacing={1}>
						<Grid item>
							<b>Filtro selecionado:</b>
						</Grid>
						<Grid item xs>
							<Chip
								label={whereData.contactsFilters?.title || "Filtro temporário"}
								style={{
									...ColorUtil.getBackgroundAndContrastColor(colors.yellow[100])
								}}
								onDelete={handleRemoveSelectedContactFilters}
							/>
						</Grid>
					</Grid>
					}
				</Grid>
			</Grid>
		</>
	)
}

export default ClientManageContacts
